import { useEffect } from 'react';
import { buildResponsiveUrl } from '@/lib/imageSrcset';
import { shouldLoadImage, getOptimalQuality } from '@/lib/connectionAware';

interface PrefetchOnIdleProps {
  images: Array<{
    src: string;
    width?: number;
    quality?: number;
  }>;
  priority?: 'low' | 'high';
}

const PrefetchOnIdle = ({ images, priority = 'low' }: PrefetchOnIdleProps) => {
  useEffect(() => {
    // Only prefetch if connection allows
    if (!shouldLoadImage('low')) {
      return;
    }

    const prefetchImages = () => {
      images.forEach(({ src, width = 800, quality }) => {
        const optimalQuality = quality || getOptimalQuality('content');
        const optimizedSrc = buildResponsiveUrl(src, { 
          width, 
          quality: optimalQuality,
          format: 'webp',
          context: 'content'
        });
        
        // Create link element for prefetch
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = optimizedSrc;
        link.as = 'image';
        document.head.appendChild(link);
        
        // Clean up after 5 seconds
        setTimeout(() => {
          if (document.head.contains(link)) {
            document.head.removeChild(link);
          }
        }, 5000);
      });
    };

    if (priority === 'high') {
      prefetchImages();
    } else {
      // Prefetch on idle for low priority
      if ('requestIdleCallback' in window) {
        requestIdleCallback(prefetchImages);
      } else {
        setTimeout(prefetchImages, 1000);
      }
    }
  }, [images, priority]);

  return null;
};

export default PrefetchOnIdle;